import React, { useCallback, useEffect, useState } from 'react'
import Cookies from 'cookies-js'
import { intl } from 'lang/intl'
import { calcAPCA } from 'apca-w3'

import { TtButton } from '@23/ui-react'

import {
  useActiveActionCardsQuery,
  useAnswerActionCardMutation,
  useClickActionCardMutation,
  useIsActionCardAnswered,
} from '../blocs/useActionCards'
import { isLinkOnlyCard } from '../models/actionCardLink'
import { useWebinarColors } from '../../Designs/blocs/useWebinarColors'

import { ActionCardForm } from './ActionCardForm'
import { ActionCardHeader } from './ActionCardHeader'
import { ActionCardAnswered } from './ActionCardAnswered'

import './ActionCard.scss'

const readViewerUuid = () =>
  // oxlint-disable-next-line typescript/no-unnecessary-condition
  Cookies.get('uuid') ?? window.localStorage.getItem('uuid')

const ActionCard = () => {
  const { data: card } = useActiveActionCardsQuery()
  const { accent } = useWebinarColors()

  const { mutateAsync: answerActionCardAPI } = useAnswerActionCardMutation(
    card?.id
  )
  const { mutate: clickActionCardAPI } = useClickActionCardMutation(card?.id)

  const [justAnswered, setJustAnswered] = useState(false)

  useEffect(() => {
    if (!card) {
      setJustAnswered(false)
    }
  }, [card])

  const isAnswered = useIsActionCardAnswered(card?.id)

  const onSubmitHandler = useCallback(
    async (values: Record<string, string | boolean>) => {
      try {
        const opts = Object.entries(values).map(([key, value]) => ({
          fieldId: +key,
          value: typeof value === 'boolean' ? value : value.trim(),
        }))

        const uuid = readViewerUuid()

        setJustAnswered(true)
        await answerActionCardAPI({ uuid, fields: opts })
      } catch (e) {
        // TODO: handle error
      }
    },
    [answerActionCardAPI]
  )

  const sendEmptyAnswer = useCallback(async () => {
    await onSubmitHandler({})
  }, [onSubmitHandler])

  const onLinkClick = useCallback(() => {
    const uuid = readViewerUuid()
    if (!uuid) return

    clickActionCardAPI({ uuid })
  }, [clickActionCardAPI])

  if (!card || !card.isActive) {
    return null
  }

  const bestHighContrast =
    Math.abs(calcAPCA('#FFFFFF', accent.color) as number) >
    Math.abs(calcAPCA('#000000', accent.color) as number)
      ? '#FFFFFF'
      : '#000000'

  return (
    <div
      className="action-card"
      style={{ '--cta-text-color': bestHighContrast }}
    >
      {isAnswered && !isLinkOnlyCard(card) ? (
        <ActionCardAnswered immediate={justAnswered} />
      ) : (
        <>
          <ActionCardHeader />
          {card.fields.length > 0 ? (
            <ActionCardForm card={card} onSubmit={onSubmitHandler} />
          ) : (
            (!!card.submitLabel || !!card.submitTargetUrl) && (
              <TtButton
                className="tray-action-cta-btn"
                size="medium"
                variant="default"
                pill
                {...(isLinkOnlyCard(card)
                  ? {
                      target: '_blank',
                      href: card.submitTargetUrl ?? undefined,
                      onClick: onLinkClick,
                    }
                  : { onClick: () => void sendEmptyAnswer() })}
              >
                {card.submitLabel ||
                  intl.formatMessage({ defaultMessage: 'Submit' })}
              </TtButton>
            )
          )}
        </>
      )}
    </div>
  )
}

export { ActionCard }
